import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";

const BUSINESS_CATEGORIES = [
  { slug: "chaniya-choli", name: "Chaniya Choli" },
  { slug: "gift-articles", name: "Gift Articles" },
  { slug: "accessories", name: "Accessories" },
  { slug: "womens-fashion", name: "Women's Fashion" },
  { slug: "mens-fashion", name: "Men's Fashion" },
  { slug: "kids", name: "Kids" },
  { slug: "trending", name: "Trending" },
];

export default function VendorRegister() {
  const { register, user, loading: authLoading, refresh, logout } = useAuth();
  const nav = useNavigate();
  // Where an already-logged-in user lands: buyers go straight to KYC (they
  // already have an account); vendors resume where they left off.
  const stepForUser = (u) => {
    if (!u) return 1;
    if (u.role === "buyer") return 2;
    if (u.role === "vendor") return ["submitted", "verified"].includes(u.kyc_status) ? 3 : 2;
    return 1;
  };
  const [step, setStep] = useState(stepForUser(user));
  const [busy, setBusy] = useState(false);
  const [acceptExhibitorTerms, setAcceptExhibitorTerms] = useState(false);
  const [acceptPayoutPolicy, setAcceptPayoutPolicy] = useState(false);
  const [noGst, setNoGst] = useState(false);
  const [acct, setAcct] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [kyc, setKyc] = useState({
    business_name: "", business_category: "", owner_name: "", phone: "", email: "",
    pan_number: "", aadhaar_number: "", gst_number: "", msme_number: "",
    business_address: "",
    bank_account_number: "", bank_ifsc: "", bank_holder_name: "",
    instagram_handle: "", facebook_handle: "", website_url: "",
  });

  const upd = (k, obj, setObj) => (e) => setObj({ ...obj, [k]: e.target.value });

  // If AuthContext finishes loading and a buyer is now logged in, jump to KYC step
  // (they already have an account and just need to submit vendor KYC)
  useEffect(() => {
    if (!authLoading && user && step === 1 && stepForUser(user) !== 1) {
      setStep(stepForUser(user));
      setKyc((prev) => ({
        ...prev,
        email: prev.email || user.email || "",
        owner_name: prev.owner_name || user.full_name || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  if (authLoading) {
    return <div className="mx-auto max-w-2xl px-4 py-20 text-center">Loading...</div>;
  }

  const createAccount = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await register({ ...acct, role: "vendor" });
      setKyc({ ...kyc, email: acct.email, phone: acct.phone, owner_name: acct.full_name });
      setStep(2); toast.success("Account created. Now submit KYC.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitKyc = async (e) => {
    e.preventDefault();
    if (!kyc.business_category) return toast.error("Please select your business category.");
    if (!acceptExhibitorTerms) return toast.error("Please accept the Vendor Agreement.");
    if (!acceptPayoutPolicy) return toast.error("Please accept the Vendor Payout Policy.");
    setBusy(true);
    try {
      await api.post("/vendor/kyc", { ...kyc, agreement_accepted: acceptExhibitorTerms, payout_policy_accepted: acceptPayoutPolicy });
      await refresh();
      setStep(3); toast.success("KYC submitted. Awaiting admin verification.");
    } catch (err) { toast.error(err.response?.data?.detail || "KYC failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12" data-testid="vendor-register-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Sell on CityShor</div>
        <h1 className="font-display text-4xl mb-2">Become an Online Exhibitor</h1>
        <p className="text-gray-600">List up to 10 products a day. Reach buyers across India without building a website of your own.</p>
      </div>

      <div className="flex items-center gap-2 mb-8">
        {[1,2,3].map((n) => (
          <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-[#F48E2E]" : "bg-gray-200"}`} />
        ))}
      </div>

      {step === 1 && (
        <form onSubmit={createAccount} className="space-y-4 p-6 bg-white rounded-2xl border border-gray-200">
          <div className="font-display text-xl mb-2">Step 1. Create Account</div>
          <div><Label>Business/Owner Name</Label><Input data-testid="vr-name" required value={acct.full_name} onChange={upd("full_name", acct, setAcct)} /></div>
          <div><Label>Email</Label><Input data-testid="vr-email" type="email" required value={acct.email} onChange={upd("email", acct, setAcct)} /></div>
          <div><Label>Phone</Label><Input data-testid="vr-phone" required value={acct.phone} onChange={upd("phone", acct, setAcct)} /></div>
          <div>
            <Label>Password</Label>
            <div className="relative">
              <Input data-testid="vr-password" type={showPassword ? "text" : "password"} required minLength={6} value={acct.password} onChange={upd("password", acct, setAcct)} className="pr-10" />
              <button
                type="button"
                data-testid="vr-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button data-testid="vr-continue" disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Continue to KYC</Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submitKyc} className="space-y-6 p-6 bg-white rounded-2xl border border-gray-200">
          <div className="font-display text-xl">Step 2. Verification (KYC)</div>
          {user && (
            <div data-testid="vr-logged-in-notice" className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2">
              <span>You are logged in as <b>{user.email}</b>. This vendor profile will be attached to that account.</span>
              <button type="button" data-testid="vr-logout" onClick={() => { logout(); setStep(1); toast.success("Logged out. Create a separate vendor account below."); }} className="underline text-[#F48E2E]">Not you? Log out</button>
            </div>
          )}

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F48E2E] mb-3">Business Details</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Business / Brand Name</Label><Input data-testid="kyc-business" required value={kyc.business_name} onChange={upd("business_name", kyc, setKyc)} /></div>
              <div className="col-span-2">
                <Label>Business Category <span className="text-red-500">*</span></Label>
                <Select value={kyc.business_category} onValueChange={(v) => setKyc({ ...kyc, business_category: v })}>
                  <SelectTrigger data-testid="kyc-business-category"><SelectValue placeholder="Select your primary category" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500 mt-1">You will only be able to list products in this category. Contact CityShor if you need to change it later.</p>
              </div>
              <div><Label>Owner Name</Label><Input data-testid="kyc-owner" required value={kyc.owner_name} onChange={upd("owner_name", kyc, setKyc)} /></div>
              <div><Label>Phone (OTP later)</Label><Input required value={kyc.phone} onChange={upd("phone", kyc, setKyc)} /></div>
              <div className="col-span-2"><Label>Email</Label><Input required value={kyc.email} onChange={upd("email", kyc, setKyc)} /></div>
              <div className="col-span-2"><Label>Business Address</Label><Textarea required value={kyc.business_address} onChange={upd("business_address", kyc, setKyc)} /></div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F48E2E] mb-3">Identity & Compliance</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>PAN Number</Label><Input data-testid="kyc-pan" required value={kyc.pan_number} onChange={upd("pan_number", kyc, setKyc)} /></div>
              <div><Label>Aadhaar Number</Label><Input data-testid="kyc-aadhaar" required value={kyc.aadhaar_number} onChange={upd("aadhaar_number", kyc, setKyc)} /></div>
              <div className={noGst ? "flex items-end" : ""}>
                {!noGst && (
                  <div className="w-full">
                    <Label>GST Number <span className="text-gray-400 font-normal">optional</span></Label>
                    <Input data-testid="kyc-gst" value={kyc.gst_number} onChange={upd("gst_number", kyc, setKyc)} placeholder="e.g. 24AAAAA0000A1Z5" />
                  </div>
                )}
                <label className={`flex items-center gap-2 cursor-pointer text-sm text-gray-700 ${noGst ? "" : "mt-2"}`}>
                  <Checkbox data-testid="kyc-no-gst" checked={noGst} onCheckedChange={(v) => { const on = !!v; setNoGst(on); if (on) setKyc((k) => ({ ...k, gst_number: "" })); }} />
                  I don't have a GST number
                </label>
              </div>
              <div><Label>MSME / Udyam <span className="text-gray-400 font-normal">optional</span></Label><Input value={kyc.msme_number} onChange={upd("msme_number", kyc, setKyc)} /></div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F48E2E] mb-3">Social Presence</div>
            <p className="text-sm text-gray-600 mb-3">Help buyers get to know your brand. Add your official handles so we can feature your drops across our channels.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Instagram Handle</Label>
                <div className="flex items-center border border-gray-200 rounded-md focus-within:ring-2 focus-within:ring-[#F48E2E]/40">
                  <span className="px-3 text-sm text-gray-500 border-r border-gray-200">@</span>
                  <input data-testid="kyc-instagram" placeholder="yourbrand" value={kyc.instagram_handle} onChange={upd("instagram_handle", kyc, setKyc)} className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none" />
                </div>
              </div>
              <div>
                <Label>Facebook Page</Label>
                <div className="flex items-center border border-gray-200 rounded-md focus-within:ring-2 focus-within:ring-[#F48E2E]/40">
                  <span className="px-3 text-sm text-gray-500 border-r border-gray-200">fb.com/</span>
                  <input data-testid="kyc-facebook" placeholder="yourbrand" value={kyc.facebook_handle} onChange={upd("facebook_handle", kyc, setKyc)} className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none" />
                </div>
              </div>
              <div className="col-span-2">
                <Label>Website / Online Store (optional)</Label>
                <Input data-testid="kyc-website" placeholder="https://" value={kyc.website_url} onChange={upd("website_url", kyc, setKyc)} />
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F48E2E] mb-3">Bank Details for Payouts</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Account Number</Label><Input data-testid="kyc-bank-acc" required value={kyc.bank_account_number} onChange={upd("bank_account_number", kyc, setKyc)} /></div>
              <div><Label>IFSC Code</Label><Input data-testid="kyc-bank-ifsc" required maxLength={11} pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" title="4 letters, then 0, then 6 alphanumeric characters (e.g. HDFC0001234)" placeholder="HDFC0001234" value={kyc.bank_ifsc} onChange={upd("bank_ifsc", kyc, setKyc)} /></div>
              <div className="col-span-2"><Label>Account Holder Name</Label><Input required value={kyc.bank_holder_name} onChange={upd("bank_holder_name", kyc, setKyc)} /></div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="kyc-accept-terms" checked={acceptExhibitorTerms} onCheckedChange={setAcceptExhibitorTerms} className="mt-1" />
              <div className="text-sm text-gray-700">
                I have read and agree to the <Link to="/legal/vendor-agreement" target="_blank" className="text-[#F48E2E] hover:underline font-medium">Vendor Agreement</Link>.
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="kyc-accept-payout" checked={acceptPayoutPolicy} onCheckedChange={setAcceptPayoutPolicy} className="mt-1" />
              <div className="text-sm text-gray-700">
                I have read and agree to the <Link to="/legal/vendor-payout-policy" target="_blank" className="text-[#F48E2E] hover:underline font-medium">Vendor Payout Policy</Link>.
              </div>
            </label>
          </div>

          <Button data-testid="kyc-submit" disabled={busy || !acceptExhibitorTerms || !acceptPayoutPolicy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-11 disabled:opacity-50">Submit for Verification</Button>
        </form>
      )}

      {step === 3 && (
        <div className="p-8 bg-white rounded-2xl border border-gray-200 text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-green-500 mb-4" />
          <div className="font-display text-2xl mb-2">Application submitted!</div>
          <p className="text-gray-600 mb-6">Our team will verify your details and email you within 24 to 48 hours. You can upload products once the verification is complete.</p>
          <Button data-testid="go-to-vendor-dash" onClick={() => nav("/vendor")} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Go to Vendor Dashboard</Button>
        </div>
      )}
    </div>
  );
}
